import type {
    CatalogImportListItem,
    CatalogImportListPage,
} from '@/api/models';

/**
 * How much history Done reads in one request: the API's largest page. One
 * page holds every import of any realistic onboarding; past it, the step
 * says how many it left out (`hidden`) instead of quietly totalling fewer.
 */
export const DONE_IMPORTS_PAGE_SIZE = 200;

/** What a set of imports brought in: the figures a subtotal and the totals add up. */
export type DoneImportFigures = {
    /** Lines in the files, excluded ones included (`rowCount`). */
    rows: number;
    /** Products created. */
    products: number;
    /** Units received. */
    units: number;
    /** The stock's net value, in integer tetri (`netCostTetri`). */
    stockValueTetri: number;
};

/** One branch's imports, newest first, and what the completed ones add up to. */
export type DoneBranchGroup = {
    branchId: string;
    branchName: string;
    imports: CatalogImportListItem[];
    /** Imports that stand; only these are counted in `subtotal`. */
    completedCount: number;
    undoneCount: number;
    subtotal: DoneImportFigures;
};

export type DoneImports = {
    groups: DoneBranchGroup[];
    /** Across branches, completed imports only. */
    totals: DoneImportFigures & {
        imports: number;
        /** Branches with at least one completed import. */
        branches: number;
    };
    /** Imports the page left out (the history holds more than one page). */
    hidden: number;
};

const NO_FIGURES: DoneImportFigures = {
    rows: 0,
    products: 0,
    units: 0,
    stockValueTetri: 0,
};

export const isUndoneImport = (item: CatalogImportListItem): boolean =>
    item.status === 'Undone';

const addImport = (
    figures: DoneImportFigures,
    item: CatalogImportListItem,
): DoneImportFigures => ({
    rows: figures.rows + item.rowCount,
    products: figures.products + item.productsCreated,
    units: figures.units + item.units,
    // Integer tetri, so the sums are exact.
    stockValueTetri: figures.stockValueTetri + item.netCostTetri,
});

const addFigures = (
    a: DoneImportFigures,
    b: DoneImportFigures,
): DoneImportFigures => ({
    rows: a.rows + b.rows,
    products: a.products + b.products,
    units: a.units + b.units,
    stockValueTetri: a.stockValueTetri + b.stockValueTetri,
});

/**
 * The organisation's import history (`GET catalog-imports` without a
 * branch, newest first) grouped for Done:
 *
 * - one group per branch, in the order the branches first appear, so the
 *   branch imported into last comes first; inside a group the list's order
 *   (newest first) is kept;
 * - an undone import stays in its group, to be shown muted, but adds nothing
 *   to the group's subtotal or to the totals;
 * - the totals add up the subtotals, and count the completed imports and the
 *   branches that have one.
 *
 * A branch archived since its import keeps its group: the stock was booked
 * all the same.
 */
export const doneImportsOf = (page: CatalogImportListPage): DoneImports => {
    const groups = new Map<string, DoneBranchGroup>();

    for (const item of page.items) {
        const group = groups.get(item.branchId) ?? {
            branchId: item.branchId,
            branchName: item.branchName,
            imports: [],
            completedCount: 0,
            undoneCount: 0,
            subtotal: NO_FIGURES,
        };
        group.imports.push(item);
        if (isUndoneImport(item)) {
            group.undoneCount += 1;
        } else {
            group.completedCount += 1;
            group.subtotal = addImport(group.subtotal, item);
        }
        groups.set(item.branchId, group);
    }

    const list = [...groups.values()];
    return {
        groups: list,
        totals: {
            ...list.reduce(
                (sum, group) => addFigures(sum, group.subtotal),
                NO_FIGURES,
            ),
            imports: list.reduce((sum, group) => sum + group.completedCount, 0),
            branches: list.filter((group) => group.completedCount > 0).length,
        },
        hidden: Math.max(0, page.totalCount - page.items.length),
    };
};
